import { IconInfo } from "@/assets/icons/geist/IconInfo.tsx";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { DialogBody, DialogClose, DialogFooter, DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { PasswordInput } from "@/components/podkit/forms/PasswordInput";
import { ToggleField } from "@/components/flexkit/ToggleField";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { useCheckScmAuthForHost } from "@/queries/environment-queries";
import { type IntegrationConfiguration, useCreateOrUpdateSCMIntegration } from "@/queries/runner-configuration-queries";
import { formatError } from "@/utils/errors";
import type { PlainMessage } from "@bufbuild/protobuf";
import { DialogDescription } from "@radix-ui/react-dialog";
import { getScmProviderName } from "frontend-shared/local-runner.ts";
import type {
    RunnerConfigurationSchema_OAuth,
    RunnerConfigurationSchema_PersonalAccessToken,
    RunnerConfigurationSchema_SCMConfigSchema,
    SCMIntegration,
    SCMIntegrationValidationResult,
} from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type FC, type FormEvent, useCallback, useId, useMemo, useState } from "react";

export const SourceControlProviderModal: FC<{
    runnerId: string;
    schema: RunnerConfigurationSchema_SCMConfigSchema;
    integration: SCMIntegration;
    onRemove: () => void;
    onBack: () => void;
    onClose: () => void;
}> = ({ runnerId, schema, integration, onRemove, onBack, onClose }) => {
    // When the schema only supports PATs (like for local runners), we don't show the toggles, and only let you configure
    // the host. It would not be possible to disable PATs, as at least one auth method is required.
    const onlyPAT = !schema.oauth && !!schema.pat;

    const createOrUpdateSCMIntegration = useCreateOrUpdateSCMIntegration(runnerId, integration.id);
    const [isDirty, setIsDirty] = useState(false);
    const [configuration, setConfiguration] = useState<IntegrationConfiguration>({
        scmId: integration.scmId,
        host: integration.host || schema.defaultHosts[0],
        oauth: integration.oauth
            ? {
                  issuerUrl: integration.oauth?.issuerUrl,
                  clientId: integration.oauth?.clientId,
                  encryptedClientSecret: integration.oauth?.encryptedClientSecret,
              }
            : undefined,
        pat: integration.pat || onlyPAT,
        virtualDirectory: integration.virtualDirectory,
    });
    const [validationErrors, setValidationErrors] = useState<SCMIntegrationValidationResult | undefined>();

    const { toast } = useToast();

    const isUpdate = Boolean(integration.id);

    const checkScmAuthForHost = useCheckScmAuthForHost();

    const handleTest = useCallback(
        async (host: string, runnerId: string) => {
            const result = await checkScmAuthForHost.mutateAsync({ host, runnerId });
            if (result?.authenticated) {
                toast({
                    title: `Already authenticated`,
                    description: "You are already authenticated with this host.",
                });
                return;
            }
            if (!result?.supportsOauth2?.authUrl) {
                console.warn("No auth URL found for host", host);
                return;
            }
            toast({
                title: `Testing authentication`,
                description: "Opening the authentication page for you.",
            });
            if (result?.supportsOauth2) {
                window.open(result?.supportsOauth2.authUrl, "_blank", "noopener");
            }
        },
        [checkScmAuthForHost, toast],
    );

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            const action = isUpdate ? "updated" : "created";
            try {
                const validationResult = await createOrUpdateSCMIntegration.mutateAsync(configuration);
                setValidationErrors(validationResult);
                if (!validationResult?.valid) {
                    return;
                }
                setIsDirty(false);

                if (configuration.oauth) {
                    await handleTest(configuration.host!, runnerId);
                } else {
                    toast({
                        title: `Provider ${action}`,
                        description: `The provider has been successfully ${action}.`,
                    });
                    onClose();
                }
            } catch (e) {
                toast({
                    title: `Failed to ${action} provider`,
                    description: formatError(e),
                });
            }
        },
        [toast, configuration, createOrUpdateSCMIntegration, isUpdate, handleTest, runnerId, onClose],
    );

    const updateConfiguration = useCallback((update: Partial<IntegrationConfiguration>) => {
        setConfiguration((prev) => ({ ...prev, ...update }));
        setIsDirty(true);
    }, []);

    const isValid = useMemo(() => {
        if (configuration.oauth) {
            const { clientId, clientSecret } = configuration.oauth;
            if (!clientId?.trim()) {
                return false;
            }
            if (!isUpdate && !clientSecret?.trim()) {
                return false;
            }
        }
        return true;
    }, [configuration.oauth, isUpdate]);

    const isSubmitDisabled = !isValid || !(onlyPAT || isDirty);

    return (
        <form onSubmit={handleSubmit} id="provider-integration" name="provider-integration">
            <DialogHeader>
                <DialogTitle>{`${isUpdate ? "Edit" : "Configure"} repository access`}</DialogTitle>
                <DialogDescription className="text-base text-content-secondary">
                    In order for your runner to access your repos, you&apos;ll need to configure your repository
                    provider (GitHub, GitLab, Bitbucket or Azure DevOps).
                </DialogDescription>
            </DialogHeader>

            <DialogBody className="flex flex-col gap-8">
                <ErrorMessage error={validationErrors?.scmIdError} />
                {onlyPAT && (
                    <div className="flex flex-row items-center gap-2">
                        <IconInfo size="sm" />
                        <Text className="text-base font-bold">
                            We currently support Personal Access Tokens with {getScmProviderName(integration.scmId)}.
                        </Text>
                    </div>
                )}
                <Host
                    schema={schema}
                    integration={integration}
                    validationError={validationErrors?.hostError}
                    onHostChange={(host) => updateConfiguration({ host })}
                />
                {integration.scmId === "azuredevops_server" && (
                    <VirtualDirectory
                        integration={integration}
                        onVirtualDirectoryChange={(virtualDirectory) => updateConfiguration({ virtualDirectory })}
                    />
                )}
                {!onlyPAT && (
                    <>
                        <OAuth
                            schema={schema.oauth}
                            host={configuration.host || "-"}
                            integration={integration}
                            validationError={validationErrors?.oauthError}
                            onAuthChange={(oauth) => updateConfiguration({ oauth })}
                        />
                        <PAT
                            schema={schema.pat}
                            integration={integration}
                            validationError={validationErrors?.patError}
                            onPatChange={(pat) => updateConfiguration({ pat })}
                        />
                    </>
                )}
            </DialogBody>

            <DialogFooter>
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-between">
                    {integration.id && (
                        <Button
                            variant="destructive"
                            type="button"
                            onClick={() => onRemove()}
                            data-tracking-id="remove-scm-integration-scm-provider-modal"
                        >
                            Remove
                        </Button>
                    )}
                    {!integration.id && (
                        <Button
                            variant="outline"
                            type="button"
                            onClick={() => onBack()}
                            data-tracking-id="back-scm-provider-modal"
                        >
                            Back
                        </Button>
                    )}
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                        <DialogClose asChild>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={createOrUpdateSCMIntegration.isPending}
                                data-tracking-id="close-scm-provider-modal"
                            >
                                Close
                            </Button>
                        </DialogClose>
                        <Button
                            autoFocus={false}
                            variant="primary"
                            type="submit"
                            data-testid="save-provider"
                            loading={createOrUpdateSCMIntegration.isPending}
                            disabled={isSubmitDisabled}
                            data-tracking-id="confirm-save-scm-provider-scm-provider-modal"
                        >
                            {configuration.oauth ? "Save & Test" : "Save"}
                        </Button>
                    </div>
                </div>
            </DialogFooter>
        </form>
    );
};

export const Host: FC<{
    schema: RunnerConfigurationSchema_SCMConfigSchema;
    integration: PlainMessage<SCMIntegration>;
    validationError: string | undefined;
    onHostChange: (host: string) => void;
}> = ({ schema, integration, validationError, onHostChange }) => {
    const isEdit = !!integration.id;
    const hostID = useId();
    const [host, setHost] = useState(integration?.host || schema.defaultHosts[0]);
    let hint = "The host of the source control provider.";
    switch (integration.scmId) {
        case "github":
            hint = "If you are using GitHub Enterprise, you can update this (e.g. mycompany.githubenterprise.com)";
            break;
        case "gitlab":
            hint =
                "If you are using GitLab Self Managed or GitLab Dedicated, you can update this (e.g. mycompany.gitlab.com)";
            break;
    }
    return (
        <div>
            <InputField
                label={`${getScmProviderName(integration.scmId)} host`}
                id={hostID}
                hint={isEdit ? "" : hint}
                error={validationError}
            >
                <Input
                    id={hostID}
                    disabled={isEdit}
                    autoFocus={!isEdit}
                    required={true}
                    title={isEdit ? "The host of existing providers can't be edited." : ""}
                    data-testid="provider-host"
                    type="text"
                    value={host}
                    onChange={(value) => {
                        setHost(value.target.value);
                        onHostChange(value.target.value);
                    }}
                    placeholder={schema.defaultHosts[0]}
                />
            </InputField>
        </div>
    );
};

type OAuthConfiguration = {
    issuerUrl?: string;
    clientId: string;
    clientSecret?: string;
    encryptedClientSecret?: Uint8Array;
};

export const OAuth: FC<{
    schema: RunnerConfigurationSchema_OAuth | undefined;
    integration: PlainMessage<SCMIntegration>;
    host: string;
    validationError: string | undefined;
    onAuthChange: (oauth?: OAuthConfiguration) => void;
}> = ({ schema, integration, validationError, onAuthChange, host }) => {
    const [configuration, setConfiguration] = useState<OAuthConfiguration | undefined>(
        integration.oauth
            ? {
                  issuerUrl: integration.oauth?.issuerUrl,
                  clientId: integration.oauth?.clientId,
                  encryptedClientSecret: integration.oauth?.encryptedClientSecret,
              }
            : undefined,
    );

    const updateConfiguration = useCallback(
        (newConfig?: OAuthConfiguration) => {
            setConfiguration(newConfig);
            onAuthChange(newConfig);
        },
        [setConfiguration, onAuthChange],
    );

    if (!schema) {
        return null;
    }

    const checked = Boolean(configuration);

    const callbackUrl = schema.callbackUrl.replace("$HOST", host);

    const onlyHasEnctryptedValue =
        Boolean(configuration?.encryptedClientSecret) && configuration?.clientSecret == undefined;

    const value =
        configuration?.clientSecret == undefined
            ? configuration?.encryptedClientSecret?.toString()
            : configuration?.clientSecret;

    const documentationLink = (scmId: string) => {
        switch (scmId) {
            case "github":
                return "https://ona.com/docs/ona/source-control/github";
            case "gitlab":
                return "https://ona.com/docs/ona/source-control/gitlab";
            case "bitbucket":
                return "https://ona.com/docs/ona/source-control/bitbucket";
            case "azuredevops":
                return "https://ona.com/docs/ona/source-control/azuredevops";
            case "azuredevops_entra":
                return "https://ona.com/docs/ona/source-control/azuredevops";
            default:
                return "https://ona.com/docs/ona/source-control/overview";
        }
    };

    const clientId = (scmId: string) => {
        switch (scmId) {
            case "azuredevops":
                return "App ID";
            default:
                return "Client ID";
        }
    };

    const isAzureDevOpsEntraSelected = integration.scmId === "azuredevops_entra";

    const authHost = isAzureDevOpsEntraSelected ? "entra.microsoft.com" : host;

    return (
        <div className="flex flex-col gap-4">
            <Divider />
            <ErrorMessage error={validationError} />
            <ToggleField
                label="Enable OAuth"
                id="enable-oauth"
                data-testid="enable-oauth"
                description="Setup an OAuth app so users can sign in quickly."
                state={checked ? "checked" : "unchecked"}
                onCheckedChange={(enabled) => {
                    if (enabled) {
                        updateConfiguration({
                            issuerUrl: integration.oauth?.issuerUrl || "",
                            clientId: integration.oauth?.clientId || "",
                            clientSecret: integration.oauth?.encryptedClientSecret ? undefined : "",
                            encryptedClientSecret: integration.oauth?.encryptedClientSecret,
                        });
                    } else {
                        updateConfiguration(undefined);
                    }
                }}
            />
            {checked && (
                <div className="flex flex-col gap-4">
                    <Text>
                        You&#x2019;ll need to setup an OAuth App at {authHost}. Copy the Authorization callback URL and
                        paste it in the OAuth App. Read the{" "}
                        <span>
                            <ExternalLink href={documentationLink(integration.scmId)}>
                                full documentation here
                            </ExternalLink>
                        </span>
                        <span>.</span>
                    </Text>
                    <InputField
                        label="Authorization callback URL"
                        id="authorization-callback-url"
                        disabled={false}
                        hint={`Copy and paste at ${authHost}`}
                    >
                        <CopyableInput
                            id="authorization-callback-url"
                            disabled={true}
                            value={callbackUrl}
                            title="Copy callback URL"
                            data-tracking-id="copy-callback-url-scm-provider-modal"
                        />
                    </InputField>
                    {isAzureDevOpsEntraSelected && (
                        <InputField
                            label="Issuer URL"
                            id="issuer-url"
                            disabled={false}
                            hint={`Your Entra ID issuer URL, e.g. https://login.microsoftonline.com/your-tenant-id/v2.0`}
                        >
                            <Input
                                id="issuer-url"
                                data-testid="oauth-issuer-url"
                                value={configuration?.issuerUrl || ""}
                                type="text"
                                onChange={(e) =>
                                    updateConfiguration({
                                        issuerUrl: e.target.value,
                                        clientId: configuration?.clientId || "",
                                        clientSecret: configuration?.clientSecret,
                                        encryptedClientSecret: configuration?.encryptedClientSecret,
                                    })
                                }
                            />
                        </InputField>
                    )}
                    <InputField
                        label={clientId(integration.scmId)}
                        id="client-id"
                        disabled={false}
                        hint={`You'll find this at ${authHost}`}
                    >
                        <Input
                            id="client-id"
                            data-testid="oauth-client-id"
                            value={configuration?.clientId}
                            type="text"
                            onChange={(e) =>
                                updateConfiguration({
                                    issuerUrl: configuration?.issuerUrl,
                                    clientId: e.target.value,
                                    clientSecret: configuration?.clientSecret,
                                    encryptedClientSecret: configuration?.encryptedClientSecret,
                                })
                            }
                        />
                    </InputField>
                    <InputField
                        label="Client secret"
                        id="client-secret"
                        disabled={false}
                        hint={`You'll find this at ${authHost}`}
                    >
                        <PasswordInput
                            id="client-secret"
                            data-testid="oauth-client-secret"
                            value={value}
                            revealable={!onlyHasEnctryptedValue}
                            onFocus={() => {
                                if (onlyHasEnctryptedValue) {
                                    updateConfiguration({
                                        issuerUrl: configuration?.issuerUrl,
                                        clientId: configuration?.clientId || "",
                                        clientSecret: "",
                                        encryptedClientSecret: configuration?.encryptedClientSecret,
                                    });
                                }
                            }}
                            onBlur={() => {
                                if (configuration?.clientSecret == "") {
                                    updateConfiguration({
                                        issuerUrl: configuration?.issuerUrl,
                                        clientId: configuration?.clientId || "",
                                        clientSecret: undefined,
                                        encryptedClientSecret: configuration?.encryptedClientSecret,
                                    });
                                }
                            }}
                            onChange={(e) =>
                                updateConfiguration({
                                    issuerUrl: configuration?.issuerUrl,
                                    clientId: configuration?.clientId || "",
                                    clientSecret: e.target.value,
                                    encryptedClientSecret: configuration?.encryptedClientSecret,
                                })
                            }
                        />
                    </InputField>
                </div>
            )}
        </div>
    );
};

export const PAT: FC<{
    schema: RunnerConfigurationSchema_PersonalAccessToken | undefined;
    integration: PlainMessage<SCMIntegration>;
    validationError: string | undefined;
    onPatChange: (pat: boolean) => void;
}> = ({ schema, integration, validationError, onPatChange }) => {
    const [checked, setChecked] = useState(integration.pat);
    const updateConfiguration = useCallback(
        (pat: boolean) => {
            setChecked(pat);
            onPatChange(pat);
        },
        [setChecked, onPatChange],
    );

    if (!schema) {
        return;
    }

    return (
        <div className="flex flex-col gap-4">
            <Divider />
            <ErrorMessage error={validationError} />
            <ToggleField
                id="enable-pat"
                data-testid="enable-pat"
                label="Personal Access Token"
                description="Allow members to sign in with a Personal Access Token."
                state={checked ? "checked" : "unchecked"}
                onCheckedChange={(checked) => {
                    updateConfiguration(checked);
                }}
            />
        </div>
    );
};

const Divider = () => {
    return <div className="h-[1px] w-full border-b border-border-light" />;
};

export const VirtualDirectory: FC<{
    integration: PlainMessage<SCMIntegration>;
    onVirtualDirectoryChange: (virtualDirectory: string) => void;
}> = ({ integration, onVirtualDirectoryChange }) => {
    const virtualDirectoryID = useId();
    const [virtualDirectory, setVirtualDirectory] = useState(integration?.virtualDirectory || "");

    const updateConfiguration = useCallback(
        (value: string) => {
            setVirtualDirectory(value);
            onVirtualDirectoryChange(value);
        },
        [onVirtualDirectoryChange],
    );

    return (
        <div className="flex flex-col gap-2">
            <InputField
                id={virtualDirectoryID}
                label="Virtual Directory"
                hint={
                    <>
                        The virtual directory path (e.g., <code>/tfs</code>). This is required for Azure DevOps Server
                        (on-premises) installations.
                    </>
                }
                error={undefined}
            >
                <Input
                    id={virtualDirectoryID}
                    data-testid="virtual-directory-input"
                    type="text"
                    value={virtualDirectory}
                    placeholder="/tfs"
                    onChange={(e) => updateConfiguration(e.target.value)}
                />
            </InputField>
            <div className="flex flex-row items-start gap-2 rounded-lg bg-surface-secondary p-3">
                <IconInfo size="sm" className="mt-0.5 shrink-0" />
                <Text className="text-sm text-content-secondary">
                    <strong>Important:</strong> Your Personal Access Token must have the scope set to{" "}
                    <strong>All accessible organizations</strong> to work with Azure DevOps Server.
                </Text>
            </div>
        </div>
    );
};
